/**
 * 地址页可筛选的 biz_type。值需与库内 explorer_tx_record.biz_type 精确一致
 *（服务端不做大小写转换）。不含 explorer 消费侧跳过的内部类型（如 ORDER_RESERVE）。
 */
export const ADDRESS_BIZ_TYPE_OPTIONS = [
    'DEPOSIT',
    'WITHDRAW',
    'TRANSFER',
    'POSITION_OPEN',
    'POSITION_CLOSE',
    'LIQUIDATION',
    'INSURANCE_PREMIUM',
    'FUNDING_FEE',
] as const;

export type AddressBizTypeFilter = (typeof ADDRESS_BIZ_TYPE_OPTIONS)[number];

/** Etherscan 风格 Tab 文案（合约/资金类主 Tab） */
export const BIZ_TYPE_TAB_LABELS: Record<AddressBizTypeFilter, string> = {
    DEPOSIT: 'Deposits',
    WITHDRAW: 'Withdrawals',
    TRANSFER: 'Transfers',
    POSITION_OPEN: 'Open Position',
    POSITION_CLOSE: 'Close Position',
    LIQUIDATION: 'Liquidations',
    INSURANCE_PREMIUM: 'Insurance Premium',
    FUNDING_FEE: 'Funding Fee',
};

/** 期权游戏：主 Tab + 子 Tab（Open / Payout） */
export const FLASH_PRODUCT_TABS = [
    {
        key: 'TREND',
        label: 'Trend',
        phases: [
            { key: 'open', label: 'Open', bizType: 'TREND_OPEN' },
            { key: 'payout', label: 'Payout', bizType: 'TREND_PAYOUT' },
        ],
    },
    {
        key: 'BEATS',
        label: 'Beats',
        phases: [
            { key: 'open', label: 'Open', bizType: 'BEATS_OPEN' },
            { key: 'payout', label: 'Payout', bizType: 'BEATS_PAYOUT' },
        ],
    },
    {
        key: 'RANGE',
        label: 'Range',
        phases: [
            { key: 'open', label: 'Open', bizType: 'RANGE_OPEN' },
            { key: 'payout', label: 'Payout', bizType: 'RANGE_PAYOUT' },
        ],
    },
    {
        key: 'PAIR',
        label: 'Pair',
        phases: [
            { key: 'open', label: 'Open', bizType: 'PAIR_OPEN' },
            { key: 'payout', label: 'Payout', bizType: 'PAIR_PAYOUT' },
        ],
    },
    {
        key: 'COMBO',
        label: 'Combo',
        phases: [
            { key: 'open', label: 'Open', bizType: 'COMBO_OPEN' },
            { key: 'payout', label: 'Payout', bizType: 'COMBO_PAYOUT' },
        ],
    },
    {
        key: 'MOVES',
        label: 'Moves',
        phases: [
            { key: 'open', label: 'Open', bizType: 'MOVES_OPEN' },
            { key: 'payout', label: 'Payout', bizType: 'MOVES_PAYOUT' },
        ],
    },
    {
        key: 'STEPS',
        label: 'Steps',
        phases: [
            { key: 'open', label: 'Open', bizType: 'STEPS_OPEN' },
            { key: 'payout', label: 'Payout', bizType: 'STEPS_PAYOUT' },
        ],
    },
] as const;

export type FlashProductKey = (typeof FLASH_PRODUCT_TABS)[number]['key'];
export type FlashBizType = (typeof FLASH_PRODUCT_TABS)[number]['phases'][number]['bizType'];

const FLASH_BIZ_TYPE_SET = new Set<string>(
    FLASH_PRODUCT_TABS.flatMap(product => product.phases.map(phase => phase.bizType)),
);

export const isFlashBizType = (value: string): value is FlashBizType =>
    FLASH_BIZ_TYPE_SET.has(value.trim().toUpperCase());

export const isKnownAddressBizType = (value: string): value is AddressBizTypeFilter =>
    (ADDRESS_BIZ_TYPE_OPTIONS as readonly string[]).includes(value);

export const findFlashProductByBizType = (
    bizType: string,
): (typeof FLASH_PRODUCT_TABS)[number] | undefined => {
    const normalized = bizType.trim().toUpperCase();
    return FLASH_PRODUCT_TABS.find(product => product.phases.some(phase => phase.bizType === normalized));
};

/** 列表摘要区 filter 文案；期权游戏为「产品 · 阶段」组合。 */
export const getAddressFilterLabel = (bizType: string): string | undefined => {
    const normalized = bizType.trim().toUpperCase();
    if ((ADDRESS_BIZ_TYPE_OPTIONS as readonly string[]).includes(normalized)) {
        return BIZ_TYPE_TAB_LABELS[normalized as AddressBizTypeFilter];
    }
    const product = findFlashProductByBizType(normalized);
    if (!product) return undefined;
    const phase = product.phases.find(p => p.bizType === normalized);
    if (!phase) return undefined;
    return `${product.label} · ${phase.label}`;
};
